import { useAdminContext } from '@/contexts/AdminContext';
import { useAuth } from '@/contexts/AuthContext';
import Icon from '@common/components/Icon';
import Logo from '@common/components/Logo';
import Modal from '@common/components/Modal';
import { Cross1Icon } from '@radix-ui/react-icons';
import React, {
    useCallback,
    useRef
} from 'react';
import BillingContent from './BillingContent';
import BrandContent from './BrandContent';
import IntegrationContent from './IntegrationContent';
import ProfileContent from './ProfileContent';
import styles from './ProfileModal.module.scss';

export enum NavKey {
    profile = 'profile',
    brand = 'brand',
    usage = 'usage',
    billing = 'billing',
    integration = 'integration',
}

const AdminProfileModal: React.FC = () => {
    const { user, userBilling } = useAuth();
    const { modal } = useAdminContext();

    const containerRef = useRef<HTMLDivElement>(null);
    const isLoadingMore = useRef(false);

    // 获取 modal 配置
    const modalConfig = modal.getModalConfig('profile');
    const isOpen = modal.isModalOpen('profile');
    const activeTab = (modalConfig.activeTab as NavKey) || NavKey.profile;

    // Unified map interface
    interface TabItem {
        name: string;
        icon: string;
        key: NavKey;
        renderContent: React.ReactElement;
    }

    // 滚动到底部时加载更多数据
    const handleScroll = useCallback(() => {
        if (!containerRef.current || isLoadingMore.current) return;

        const {
            scrollTop, scrollHeight, clientHeight
        } = containerRef.current;
        const isNearBottom = scrollTop + clientHeight >= scrollHeight - 50; // 距离底部50px时触发

        if (isNearBottom) {
            if (activeTab === 'usage') {
                const { pointsHistory } = userBilling;
                if (pointsHistory?.pagination?.hasNext) {
                    isLoadingMore.current = true;
                    const nextPage = pointsHistory.pagination.page + 1;
                    userBilling.fetchPointsHistory(nextPage, pointsHistory.pagination.size)
                        .finally(() => {
                            isLoadingMore.current = false;
                        });
                }
            } else if (activeTab === 'billing') {
                const { invoices } = userBilling;
                if (invoices?.pagination?.hasNext) {
                    isLoadingMore.current = true;
                    const nextPage = invoices.pagination.page + 1;
                    userBilling.fetchInvoices(nextPage, invoices.pagination.size)
                        .finally(() => {
                            isLoadingMore.current = false;
                        });
                }
            }
        }
    }, [userBilling, activeTab]);

    // 当切换到 usage 或 billing tab 时，添加滚动监听器
    React.useEffect(() => {
        if ((activeTab === 'usage' || activeTab === 'billing') && containerRef.current) {
            const container = containerRef.current;
            container.addEventListener('scroll', handleScroll);

            return () => {
                container.removeEventListener('scroll', handleScroll);
            };
        }
    }, [activeTab, handleScroll]);

    if (!isOpen || !user) {
        return null;
    }

    // Unified tab map with navigation and content
    const TabMap: Record<any, TabItem> = {
        profile: {
            name: 'Profile',
            icon: 'profile',
            key: NavKey.profile,
            renderContent: <ProfileContent onClose={() => modal.closeModal('profile')} />,
        },
        brand: {
            name: 'Basic Information',
            icon: 'brand',
            key: NavKey.brand,
            renderContent: <BrandContent />,
        },
        // usage: {
        //     name: 'Usage details',
        //     icon: 'details',
        //     key: NavKey.usage,
        //     renderContent: <UsageContent onClose={onClose} />,
        // },
        billing: {
            name: 'Plans & Billing',
            icon: 'billing',
            key: NavKey.billing,
            renderContent: <BillingContent onClose={() => modal.closeModal('profile')} />,
        },
        integration: {
            name: 'Integrations',
            icon: 'connect',
            key: NavKey.integration,
            renderContent: <IntegrationContent />,
        },
    };

    const Category = [{
        name: 'Account',
        tabs: [NavKey.profile]
    },{
        name: 'Brand',
        tabs: [NavKey.brand, NavKey.integration]
    },{
        name: 'Subscription',
        tabs: [NavKey.billing]
    }];

    return (
        <Modal onClose={() => modal.closeModal('profile')} hideCloseButton>
            <div className={styles.profileModal}>
                {/* Left Sidebar */}
                <div className={styles.sidebar}>
                    <div className={styles.logo}>
                        <Logo size="1.6rem" />
                    </div>

                    <nav className={styles.navigation}>
                        {Category.map((item) => (
                            <div key={item.name} className={styles.navCategory}>
                                <span className={styles.navCategoryTitle}>{item.name}</span>
                                {item.tabs.map((tabKey) => (
                                    <button
                                        key={tabKey}
                                        className={`${styles.navItem} ${activeTab === tabKey ? styles.active : ''}`}
                                        onClick={() => modal.updateModal('profile', { activeTab: tabKey })}
                                    >
                                        <Icon name={TabMap[tabKey].icon} size={20} />
                                        <span>{TabMap[tabKey].name}</span>
                                    </button>
                                ))}
                            </div>
                        ))}
                    </nav>
                </div>

                {/* Right Content Area */}
                {/* Header */}
                <div className={styles.container} ref={containerRef}>
                    <div className={styles.header}>
                        <h2 className={styles.title}>
                            {TabMap[activeTab].name}
                        </h2>
                        <div className={styles.modalClose} onClick={() => modal.closeModal('profile')}>
                            <Cross1Icon width={14} height={14} />
                        </div>
                    </div>
                    <div className={styles.content}>
                        {TabMap[activeTab].renderContent}
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default AdminProfileModal;